import React, { useMemo, useRef, useState } from 'react';
import { Popover, styled as styledMaterial } from '@mui/material';
import { TelephoneAlert } from 'components/views/atoms/TelephoneAlert';
import styled from '@emotion/styled';
import { TelephoneButton } from '../TelephoneButton';

export type TelephoneState = 'ready' | 'ringing' | 'calling' | 'talking' | 'disabled';

export type TelephonePopoverButtonProps = {
    state: Record<string, TelephoneState>;
    onClick?: (tag: string) => void;
};

const StyledPopover = styledMaterial(Popover)`
  .MuiPopover-paper { 
    border-radius: 15px;
    padding: 1rem; 
  }
`;

const PhoneList = styled.ul({
    listStyle: 'none',
    display: 'flex',
    flexFlow: 'column',
    margin: 0,
    padding: 0,
});

const PhoneListItem = styled.li({
    display: 'flex',
    alignItems: 'center',
    padding: '5px 0',
});

const PhoneModalSpan = styled.span`
    margin-left: 5px;
    font-size: 1.4rem;
    font-family: Cabin Sketch, arial, serif;
`;

export const TelephonePopoverButton = (props: TelephonePopoverButtonProps) => {
    const { state, onClick, ...otherProps } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const phoneListRef = useRef<HTMLUListElement>(null);
    const togglePopover = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (event) {
            setAnchorEl(event.currentTarget);
        }
        setIsOpen(!isOpen);
    };

    const handleClose = () => {
        setIsOpen(false);
        setAnchorEl(null);
    };

    const { buttonState, ringingCount } = useMemo(() => {
        const allStatuses = Object.values(state);
        const ringingCount = allStatuses.filter((status) => status === 'ringing').length;

        let buttonState: TelephoneState = 'disabled';
        if (ringingCount > 0) {
            buttonState = 'ringing';
        } else if (allStatuses.length > 0) {
            buttonState = 'ready';
        }
        return { buttonState, ringingCount };
    }, [state]);

    return (
        <div style={{ position: 'relative' }}>
            {(ringingCount > 0 && !isOpen) && <TelephoneAlert variant="outlined" severity="error">{ringingCount === 1 ? '1 call' : `${ringingCount} calls`}</TelephoneAlert>}
            <TelephoneButton state={buttonState} aria-describedby="cockpit-button" onClick={(e) => togglePopover(e)} {...otherProps} />
            <StyledPopover
                id="cockpit-button"
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <PhoneList ref={phoneListRef}>
                    {Object.keys(state).map((tag) => (
                        <PhoneListItem key={tag}>
                            <TelephoneButton state={state[tag]} frameHeight={'2vh'} frameWidth={'2vh'} onClick={onClick && (() => onClick(tag))} />
                            <PhoneModalSpan>{tag}</PhoneModalSpan>
                        </PhoneListItem>
                    ))}
                </PhoneList>
            </StyledPopover>
        </div>
    );
};
